import { useQuery } from '@tanstack/react-query';
import {
	Table,
	TableRow,
	TableHeader,
	TableHead,
	TableCell,
	TableBody
} from 'features/components/Table';
import { makeRequest } from 'features/entry-form-v2/data/makeRequest';
import { useGetNamesFromUserIdsQuery } from 'features/entry-form-v2/data/useGetNamesFromUserIdsQuery';
import { useLanguage } from 'features/hooks/useLanguage';
import { DateTime } from 'luxon';
import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { LanguageType } from 'types';

export const CodelistHistoryTab = () => {
	const { projectId } = useParams();
	const language = useLanguage();
	const translation = DICTIONARY[language];

	const getCodelistHistoryQuery = useGetCodelistHistory(projectId as string);

	const userIdSet = useMemo(
		() => new Set(getCodelistHistoryQuery.data?.events.map(event => event.userId)),
		[getCodelistHistoryQuery.data]
	);

	const getNamesFromUserIdsQuery = useGetNamesFromUserIdsQuery({
		userIds: Array.from(userIdSet)
	});

	const isLoading = getCodelistHistoryQuery.isLoading || getNamesFromUserIdsQuery.isLoading;

	if (isLoading) {
		return <div>Loading...</div>;
	}

	return (
		<div className="flex flex-col gap-4 overflow-y-auto max-w-[1000px]">
			<Table>
				<TableHeader>
					<TableRow>
						<TableHead>{translation.createdAt}</TableHead>
						<TableHead>{translation.action}</TableHead>
						<TableHead>{translation.user}</TableHead>
					</TableRow>
				</TableHeader>

				<TableBody>
					{getCodelistHistoryQuery.data?.events.map(event => (
						<TableRow key={event.id}>
							<TableCell>
								{DateTime.fromISO(event.createdAt).toLocaleString(
									DateTime.DATETIME_MED_WITH_SECONDS
								)}
							</TableCell>
							<TableCell>{formatAction(event.eventType, translation)}</TableCell>
							<TableCell>
								{getNamesFromUserIdsQuery.data?.namesFromUserIds[event.userId]}
							</TableCell>
						</TableRow>
					))}
				</TableBody>
			</Table>
		</div>
	);
};

const DICTIONARY = {
	[LanguageType.English]: {
		createdAt: 'Timestamp',
		user: 'User',
		action: 'Action',
		getRecord: 'Viewed record',
		createRecord: 'Created record',
		updateRecord: 'Updated record',
		searchRecords: 'Searched records'
	},
	[LanguageType.Norwegian]: {
		createdAt: 'Tidspunkt',
		user: 'Bruker',
		action: 'Hendelse',
		getRecord: 'Sett kodeliste',
		createRecord: 'Opprettet kodeliste',
		updateRecord: 'Oppdatert kodeliste',
		searchRecords: 'Søkte i kodelister'
	}
};

const useGetCodelistHistory = (projectId: string) => {
	return useQuery({
		queryKey: ['codelist-audit-log', projectId],
		queryFn: () =>
			makeRequest<GetCodelistAuditLogResponse>({
				method: 'getCodelistAuditLog',
				service: 'cde',
				data: {
					projectId,
					offset: 0,
					limit: 100
				}
			})
	});
};

type EntityType = 'RECORD';
type EventType = 'GET_RECORD' | 'CREATE_RECORD' | 'UPDATE_RECORD' | 'SEARCH_RECORDS';

type AuditEvent = {
	createdAt: string;
	entityType: EntityType;
	eventType: EventType;
	id: string;
	projectId: string;
	userId: string;
};
type GetCodelistAuditLogResponse = {
	events: AuditEvent[];
};

const formatAction = (
	action: EventType,
	translation: (typeof DICTIONARY)[LanguageType.English]
) => {
	switch (action) {
		case 'GET_RECORD':
			return translation.getRecord;
		case 'CREATE_RECORD':
			return translation.createRecord;
		case 'UPDATE_RECORD':
			return translation.updateRecord;
		case 'SEARCH_RECORDS':
			return translation.searchRecords;
	}
};
